import { create } from 'zustand';
import { modulesApi, tenantApi, AppModule } from '@/lib/api';

export interface License {
  key: string;
  valid_from?: string;
  valid_until?: string;
}

interface ModulesState {
  modules: AppModule[];
  licensedModules: AppModule[];
  licenses: License[];
  loading: boolean;
  error: string | null;
  _isLoadingModules: boolean; // Internal flag to prevent concurrent loads
  _isLoadingLicenses: boolean; // Internal flag to prevent concurrent loads
  loadModules: () => Promise<void>;
  loadLicenses: () => Promise<void>;
  /** Hydrate from general-info (licenses + modules); computes licensedModules */
  setFromBootstrap: (licenses: License[], modules: AppModule[]) => void;
  getModule: (key: string) => AppModule | undefined;
  getModuleSubmenus: (key: string) => string[];
  hasLicense: (key: string) => boolean;
  isLicenseValid: (key: string) => boolean;
  clearModules: () => void;
}

export const useModulesStore = create<ModulesState>((set, get) => ({
  modules: [],
  licensedModules: [],
  licenses: [],
  loading: false,
  error: null,
  _isLoadingModules: false,
  _isLoadingLicenses: false,
  
  loadLicenses: async () => {
    // Prevent concurrent loads
    if (get()._isLoadingLicenses) return;
    set({ _isLoadingLicenses: true });
    
    try {
      // Load licenses from master DB (tenants table)
      const response = await tenantApi.getLicenses();
      const rawLicenses = response.data?.data || [];
      
      // Normalize licenses - API may return array of strings or array of objects
      const licenses: License[] = rawLicenses.map((l: string | License) => {
        if (typeof l === 'string') {
          return { key: l };
        }
        return l;
      });
      
      set({ licenses, _isLoadingLicenses: false });
    } catch (err: any) {
      console.error('Failed to load licenses:', err);
      set({ licenses: [], _isLoadingLicenses: false });
    }
  },

  setFromBootstrap: (licenses, allModules) => {
    const now = new Date();
    const licensed = allModules.filter((module: AppModule) => {
      if (module.always_available) return true;
      const license = licenses.find((l: License) => l.key === module.key);
      if (!license) return false;
      if (license.valid_from) {
        const validFrom = new Date(license.valid_from);
        if (now < validFrom) return false;
      }
      if (license.valid_until) {
        const validUntil = new Date(license.valid_until);
        if (now > validUntil) return false;
      }
      return true;
    });
    set({ modules: allModules, licenses, licensedModules: licensed });
  },

  loadModules: async () => {
    // Prevent concurrent loads
    if (get()._isLoadingModules) return;
    
    // Skip if already loaded
    if (get().licensedModules.length > 0) return;
    
    set({ loading: true, error: null, _isLoadingModules: true });
    try {
      // Load licenses if not already loaded
      if (get().licenses.length === 0) {
        await get().loadLicenses();
      }
      
      const response = await modulesApi.list();
      const allModules = response.data?.data || [];
      
      set({ modules: allModules });
      
      // Filter by licenses and validity
      const { licenses } = get();
      const now = new Date();
      
      const licensed = allModules.filter((module: AppModule) => {
        // Always available modules (like dashboard)
        if (module.always_available) return true;
        
        const license = licenses.find(l => l.key === module.key);
        if (!license) return false;
        
        // Check validity
        if (license.valid_from) {
          const validFrom = new Date(license.valid_from);
          if (now < validFrom) return false;
        }
        if (license.valid_until) {
          const validUntil = new Date(license.valid_until);
          if (now > validUntil) return false;
        }
        
        return true;
      });
      
      set({ licensedModules: licensed, loading: false, _isLoadingModules: false });
    } catch (err: any) {
      console.error('Failed to load modules:', err);
      set({
        modules: [],
        licensedModules: [],
        loading: false,
        _isLoadingModules: false,
        error: err.message || 'Failed to load modules'
      });
    }
  },
  
  getModule: (key: string) => {
    const { modules } = get();
    return modules.find(m => m.key === key);
  },
  
  getModuleSubmenus: (key: string) => {
    const module = get().getModule(key);
    return module?.submenus || [];
  },
  
  hasLicense: (key: string) => {
    const { licenses } = get();
    return licenses.some(l => l.key === key);
  },
  
  isLicenseValid: (key: string) => {
    const { licenses, modules } = get();
    
    // Check if module is always available
    const module = modules.find(m => m.key === key);
    if (module?.always_available) return true;
    
    const license = licenses.find(l => l.key === key);
    if (!license) return false;
    
    const now = new Date();
    if (license.valid_from) {
      const validFrom = new Date(license.valid_from);
      if (now < validFrom) return false;
    }
    if (license.valid_until) {
      const validUntil = new Date(license.valid_until);
      if (now > validUntil) return false;
    }
    
    return true;
  },
  
  clearModules: () => {
    set({
      modules: [],
      licensedModules: [],
      licenses: [],
      error: null,
      _isLoadingModules: false,
      _isLoadingLicenses: false
    });
  },
}));
